import React, { useEffect, useState, memo, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    Switch,
    Tooltip,
    alpha,
    useTheme,
    CircularProgress,
    Stack,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Fade,
    Modal,
    Popper,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import LabelIcon from '@mui/icons-material/Label';
import CommentIcon from '@mui/icons-material/Comment';
import ReportIcon from '@mui/icons-material/Report';
import HistoryIcon from '@mui/icons-material/History';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CodeIcon from '@mui/icons-material/Code';
import RouterIcon from '@mui/icons-material/Router';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MapIcon from '@mui/icons-material/Map';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import FilterListIcon from '@mui/icons-material/FilterList';
import Chip from '@mui/material/Chip';
import blankDiagramSvg from '../../Components/Assets/Blank diagram.svg';
import ShowChartIcon from '@mui/icons-material/ShowChart';

interface ControlPanelProps {
    filter: string;
    setFilter: (value: string) => void;
    selectedLabels: string[];
    setSelectedLabels: React.Dispatch<React.SetStateAction<string[]>>;
    uniqueLabels: string[];
    selectedModems: string[];
    setSelectedModems: React.Dispatch<React.SetStateAction<string[]>>;
    uniqueModems: string[];
    selectionMode: boolean;
    handleToggleSelection: () => void;
    selectedDevices: string[];
    setSelectedDevices: React.Dispatch<React.SetStateAction<string[]>>;
    setSelectionMode: React.Dispatch<React.SetStateAction<boolean>>;
    allDeviceBoxes: string[];
    setAddDeviceDialogOpen: (open: boolean) => void;
    totalDevices: number;
    onRemoveDevices: () => void;
    onManageLabels: () => void;
    onAddLabels: () => void;
    onAddComment: () => void;
    onReportIssue: () => void;
    onViewHistory: () => void;
    onOpenScriptsPanel: () => void;
    onLoadCharts: () => void;
    isFiltering: boolean;
    labelFilterMode: 'any' | 'all' | 'exact' | 'exclude';
    setLabelFilterMode: (mode: 'any' | 'all' | 'exact' | 'exclude') => void;
    filterListBySelection: boolean;
    toggleFilterListBySelection: () => void;
}

const ControlPanel: React.FC<ControlPanelProps> = memo(({
    filter,
    setFilter,
    selectedLabels,
    setSelectedLabels,
    uniqueLabels,
    selectedModems,
    setSelectedModems,
    uniqueModems,
    selectionMode,
    handleToggleSelection,
    selectedDevices,
    setSelectedDevices,
    setSelectionMode,
    allDeviceBoxes,
    setAddDeviceDialogOpen,
    totalDevices,
    onRemoveDevices,
    onManageLabels,
    onAddLabels,
    onAddComment,
    onReportIssue,
    onViewHistory,
    onOpenScriptsPanel,
    onLoadCharts,
    isFiltering = false,
    labelFilterMode = 'any',
    setLabelFilterMode,
    filterListBySelection,
    toggleFilterListBySelection
}) => {
    const theme = useTheme();
    const [isAdmin, setIsAdmin] = useState(false);
    const [expandedFilter, setExpandedFilter] = useState<string | false>('labels');
    const [warehouseMapOpen, setWarehouseMapOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [searchLabel, setSearchLabel] = useState('');
    const [bulkSelectInput, setBulkSelectInput] = useState('');
    const [invalidBoxesDialogOpen, setInvalidBoxesDialogOpen] = useState(false);
    const [invalidBoxesToShow, setInvalidBoxesToShow] = useState<string[]>([]);
    
    const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedFilter(isExpanded ? panel : false);
    };
    
    const handleOpenWarehouseMap = () => {
        setWarehouseMapOpen(true);
    };
    
    const handleCloseWarehouseMap = () => {
        setWarehouseMapOpen(false);
    };
    
    const handleHoverOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    
    const handleHoverClose = () => {
        setAnchorEl(null);
    };
    
    const open = Boolean(anchorEl);
    
    useEffect(() => {
        // Check if user has admin role
        const userRole = localStorage.getItem('role');
        setIsAdmin(userRole === 'admin');
    }, []);

    const handleLabelToggle = useCallback((label: string) => {
        setSelectedLabels(prev => 
            prev.includes(label)
                ? prev.filter(l => l !== label)
                : [...prev, label]
        );
    }, [setSelectedLabels]);

    const handleModemToggle = useCallback((modem: string) => {
        setSelectedModems(prev => 
            prev.includes(modem)
                ? prev.filter(m => m !== modem)
                : [...prev, modem]
        );
    }, [setSelectedModems]);

    const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setFilter(e.target.value);
    }, [setFilter]);

    // Filter labels based on search term
    const filteredLabels = uniqueLabels.filter(label => 
        label.toLowerCase().includes(searchLabel.toLowerCase())
    );
    
    // Label filter mode descriptions
    const filterModeDescriptions = {
        'any': 'Shows devices that have ANY of the selected labels',
        'all': 'Shows devices that have ALL of the selected labels (can have others too)',
        'exact': 'Shows devices that have ONLY the selected labels, nothing else',
        'exclude': 'Shows devices that DON\'T have any of the selected labels'
    };
    
    // Handle label filter mode change
    const handleFilterModeChange = (mode: 'any' | 'all' | 'exact' | 'exclude') => {
        setLabelFilterMode(mode);
    };

    const handleBulkSelect = useCallback(() => {
        const inputBoxes = bulkSelectInput
            .split(/[\s,]+/)
            .map(box => box.trim())
            .filter(box => box !== '');

        if (inputBoxes.length === 0) {
            return;
        }

        const uniqueInputBoxes = [...new Set(inputBoxes)];

        const validBoxes: string[] = [];
        const invalidBoxes: string[] = [];

        uniqueInputBoxes.forEach(box => {
            if (allDeviceBoxes.includes(box)) {
                validBoxes.push(box);
            } else {
                invalidBoxes.push(box);
            }
        });

        if (validBoxes.length > 0) {
            setSelectedDevices(prevSelected => {
                const newSelection = new Set(prevSelected);
                validBoxes.forEach(box => newSelection.add(box));
                return Array.from(newSelection);
            });
            setSelectionMode(true);
        }

        if (invalidBoxes.length > 0) {
            setInvalidBoxesToShow(invalidBoxes);
            setInvalidBoxesDialogOpen(true);
        }

        setBulkSelectInput('');
    }, [bulkSelectInput, allDeviceBoxes, setSelectionMode, setSelectedDevices]);

    return (
        <Paper
            elevation={3}
            sx={{ 
                position: 'sticky',
                top: 16,
                float: 'right',
                right: 24,
                width: 230,
                maxHeight: 'calc(100vh - px)',
                display: { xs: 'none', md: 'block' },
                borderRadius: '5px',
                bgcolor: theme.name === 'dark' 
                    ? alpha(theme.palette.background.paper, 0.95)
                    : theme.palette.background.paper,
                backdropFilter: 'blur(8px)',
                boxShadow: theme.name === 'dark'
                    ? '0 0 20px rgba(0, 0, 0, 0.4)'
                    : '-4px 0 20px rgba(0, 0, 0, 0.1)',
                zIndex: 100,
                p: 1.5,
                overflowY: 'auto',
                border: `1px solid ${theme.palette.borders?.primary || theme.palette.divider}`,
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography 
                    variant="subtitle1"
                    sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        color: theme.name === 'dark' ? theme.palette.primary.light : theme.palette.primary.main
                    }}
                >
                    <FilterAltIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                    Control Panel
                </Typography>
                
                <IconButton 
                    size="small" 
                    color="primary"
                    onClick={handleOpenWarehouseMap}
                    onMouseEnter={handleHoverOpen}
                    onMouseLeave={handleHoverClose}
                    aria-describedby="warehouse-map-preview"
                    sx={{ 
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1)
                        }
                    }}
                >
                    <WarehouseIcon fontSize="small" />
                </IconButton>
                
                {/* Hover preview */}
                <Popper 
                    id="warehouse-map-preview" 
                    open={open} 
                    anchorEl={anchorEl}
                    placement="right-start"
                    modifiers={[
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 8],
                            },
                        },
                    ]}
                    sx={{ zIndex: 1300 }}
                >
                    <Paper 
                        elevation={3} 
                        sx={{ 
                            p: 1.5, 
                            width: 400, 
                            height: 320,
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.paper,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        onMouseEnter={handleHoverOpen}
                        onMouseLeave={handleHoverClose}
                    >
                        <Box sx={{
                            position: 'relative',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: `1px dashed ${alpha(theme.palette.divider, 0.5)}`,
                            borderRadius: 1,
                            mt: 1,
                            overflow: 'hidden'
                        }}>
                            <img src={blankDiagramSvg} alt="Warehouse Map" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    width: 24,
                                    height: 24,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    borderRadius: '50%',
                                }}
                            >
                                <OpenInFullIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                            </Box>
                        </Box>
                        <Typography variant="caption" sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 1 }}>
                            Click on warehouse icon for full map
                        </Typography>
                    </Paper>
                </Popper>
                
                {/* Full size modal map */}
                <Modal
                    open={warehouseMapOpen}
                    onClose={handleCloseWarehouseMap}
                    closeAfterTransition
                >
                    <Fade in={warehouseMapOpen}>
                        <Box sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 800,
                            height: 700,
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: 4,
                            borderRadius: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Typography variant="h5" component="h2" gutterBottom>
                                Warehouse Map
                            </Typography>
                            <Box sx={{
                                width: '100%',
                                height: '90%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `1px dashed ${theme.palette.divider}`,
                                borderRadius: 1,
                                bgcolor: alpha(theme.palette.background.default, 0.5),
                                overflow: 'hidden'
                            }}>
                                <img src={blankDiagramSvg} alt="Warehouse Map" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                            </Box>
                            <Button 
                                variant="contained" 
                                onClick={handleCloseWarehouseMap} 
                                sx={{ mt: 3, px: 4, py: 1 }}
                                size="large"
                            >
                                Close
                            </Button>
                        </Box>
                    </Fade>
                </Modal>
            </Box>
            
            {/* Box number filter */}
            <Box sx={{ mb: 2 }}>
                <Tooltip placement="right" title="Click to filter devices by box number">
                    <Typography variant="caption" sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
                        Filter by Box Number
                    </Typography>
                </Tooltip>
                <Box sx={{ position: 'relative' }}>
                    <TextField
                        fullWidth
                        placeholder="Box number..."
                        variant="outlined"
                        size="small"
                        value={filter}
                        onChange={handleFilterChange}
                        InputProps={{
                            startAdornment: (
                                <SearchIcon color="action" sx={{ mr: 0.5, fontSize: '1rem' }} />
                            ),
                        }}
                    />
                    {isFiltering && (
                        <CircularProgress 
                            size={16} 
                            sx={{ 
                                position: 'absolute', 
                                right: '10px', 
                                top: '50%', 
                                transform: 'translateY(-50%)' 
                            }} 
                        />
                    )}
                </Box>
            </Box>

            {/* Label filter with accordion - Enhanced version */}
            <Accordion
                expanded={expandedFilter === 'labels'}
                onChange={handleAccordionChange('labels')}
                disableGutters
                elevation={0}
                sx={{
                    mb: 1,
                    '&:before': { display: 'none' },
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    borderRadius: 1,
                    backgroundColor: 'transparent'
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                        minHeight: 40,
                        '& .MuiAccordionSummary-content': { margin: '6px 0' }
                    }}
                >
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            fontWeight: selectedLabels.length > 0 ? 'bold' : 'normal',
                            color: selectedLabels.length > 0 ? theme.palette.primary.main : 'inherit'
                        }}
                    >
                        <LabelIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                        Filter by Labels {selectedLabels.length > 0 && `(${selectedLabels.length})`}
                        {isFiltering && selectedLabels.length > 0 && (
                            <CircularProgress size={14} sx={{ ml: 1 }} />
                        )}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1, pt: 0 }}>
                    {/* Filter Mode Selector */}
                    <Box sx={{ mb: 1.5 }}>
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(['any', 'all', 'exact', 'exclude'] as const).map((mode) => (
                                <Tooltip key={mode} title={filterModeDescriptions[mode]}>
                                    <Chip
                                        label={mode.charAt(0).toUpperCase() + mode.slice(1)}
                                        size="small"
                                        color={labelFilterMode === mode ? "primary" : "default"}
                                        onClick={() => handleFilterModeChange(mode)}
                                        sx={{ 
                                            fontSize: '0.7rem',
                                            fontWeight: labelFilterMode === mode ? 'bold' : 'normal',
                                            backgroundColor: labelFilterMode === mode ? 
                                                alpha(theme.palette.primary.main, theme.name === 'dark' ? 0.25 : 0.1) : 'transparent',
                                            border: `1px solid ${labelFilterMode === mode ? 
                                                theme.palette.primary.main : 
                                                alpha(theme.palette.divider, 0.5)}`,
                                            color: labelFilterMode === mode && theme.name === 'dark' ? '#FFFFFF' : undefined,
                                        }}
                                    />
                                </Tooltip>
                            ))}
                        </Box>
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                mt: 0.5, 
                                display: 'block', 
                                color: 'text.secondary',
                                fontSize: '0.7rem'
                            }}
                        >
                            
                        </Typography>
                    </Box>

                    {/* Label Search Box */}
                    <TextField
                        fullWidth
                        placeholder="Search labels..."
                        variant="outlined"
                        size="small"
                        value={searchLabel}
                        onChange={(e) => setSearchLabel(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <SearchIcon color="action" sx={{ mr: 0.5, fontSize: '1rem' }} />
                            ),
                        }}
                        sx={{ mb: 1 }}
                    />

                    {/* Selected Labels Quick View */}
                    {selectedLabels.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, display: 'block' }}>
                                Selected:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selectedLabels.map(label => (
                                    <Chip
                                        key={label}
                                        label={label.length > 15 ? `${label.substring(0, 13)}...` : label}
                                        size="small"
                                        onDelete={() => handleLabelToggle(label)}
                                        color="primary"
                                        sx={{ fontSize: '0.7rem' }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Label List */}
                    <Paper 
                        variant="outlined" 
                        sx={{ 
                            maxHeight: '150px', 
                            overflowY: 'auto',
                            p: 1,
                            border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                        }}
                    >
                        {filteredLabels.length > 0 ? (
                            filteredLabels.map((label) => (
                                <FormControlLabel
                                    key={label}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={selectedLabels.includes(label)}
                                            onChange={() => handleLabelToggle(label)}
                                        />
                                    }
                                    label={
                                        <Box sx={{ 
                                            width: '100%',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            <Tooltip title={label} placement="right">
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ 
                                                        fontSize: '0.8rem',
                                                        lineHeight: '1.2'
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                            </Tooltip>
                                        </Box>
                                    }
                                    sx={{ 
                                        display: 'flex',
                                        margin: 0,
                                        py: 0.5,
                                        '& .MuiCheckbox-root': {
                                            py: 0.5,
                                            px: 0.5
                                        },
                                        '& .MuiFormControlLabel-label': {
                                            width: '100%'
                                        }
                                    }}
                                />
                            ))
                        ) : (
                            <Typography variant="body2" sx={{ textAlign: 'center', py: 1, color: 'text.secondary' }}>
                                No labels match your search
                            </Typography>
                        )}
                    </Paper>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        {selectedLabels.length > 0 && (
                            <Button
                                size="small"
                                onClick={() => setSelectedLabels([])}
                                fullWidth
                                variant="outlined"
                                color="error"
                            >
                                Clear All
                            </Button>
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>
            
            {/* Modem filter with accordion */}
            <Accordion
                expanded={expandedFilter === 'modems'}
                onChange={handleAccordionChange('modems')}
                disableGutters
                elevation={0}
                sx={{
                    mb: 1,
                    '&:before': { display: 'none' },
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    borderRadius: 1,
                    backgroundColor: 'transparent'
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                        minHeight: 40,
                        '& .MuiAccordionSummary-content': { margin: '6px 0' }
                    }}
                >
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            fontWeight: selectedModems.length > 0 ? 'bold' : 'normal',
                            color: selectedModems.length > 0 ? theme.palette.primary.main : 'inherit'
                        }}
                    >
                        <RouterIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                        Filter by Modems {selectedModems.length > 0 && `(${selectedModems.length})`}
                        {isFiltering && selectedModems.length > 0 && (
                            <CircularProgress size={14} sx={{ ml: 1 }} />
                        )}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1, pt: 0 }}>
                    <Paper 
                        variant="outlined" 
                        sx={{ 
                            maxHeight: '200px', 
                            overflowY: 'auto',
                            p: 1,
                            border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                        }}
                    >
                        {uniqueModems.map((modem) => (
                            <FormControlLabel
                                key={modem}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={selectedModems.includes(modem)}
                                        onChange={() => handleModemToggle(modem)}
                                    />
                                }
                                label={
                                    <Box sx={{ 
                                        width: '100%',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        <Tooltip title={modem} placement="right">
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    fontSize: '0.8rem',
                                                    lineHeight: '1.2'
                                                }}
                                            >
                                                {modem.length > 15 ? `${modem.substring(0, 12)}...` : modem}
                                            </Typography>
                                        </Tooltip>
                                    </Box>
                                }
                                sx={{ 
                                    display: 'flex',
                                    margin: 0,
                                    py: 0.5,
                                    '& .MuiCheckbox-root': {
                                        py: 0.5,
                                        px: 0.5
                                    },
                                    '& .MuiFormControlLabel-label': {
                                        width: '100%'
                                    }
                                }}
                            />
                        ))}
                    </Paper>
                    {selectedModems.length > 0 && (
                        <Button
                            size="small"
                            onClick={() => setSelectedModems([])}
                            sx={{ mt: 1 }}
                            fullWidth
                            variant="outlined"
                        >
                            Clear Modems
                        </Button>
                    )}
                </AccordionDetails>
            </Accordion>
            
            {/* Selection mode - visible only for admin */}
            {isAdmin && (
            <Box sx={{ mb: 3 }}>
                <FormControlLabel
                    control={
                        <Switch 
                            checked={selectionMode} 
                            onChange={handleToggleSelection}
                            color="primary"
                            size="small"
                        />
                    }
                    label={
                        <Typography variant="body2">
                            Selection Mode {selectionMode && `(${selectedDevices.length})`}
                        </Typography>
                    }
                    sx={{ mx: 0 }}
                />
                
                <Box sx={{ mt: 1, mb: 2 }}>
                    <Typography variant="caption">Bulk add to selection</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Box numbers..."
                            value={bulkSelectInput}
                            onChange={(e) => setBulkSelectInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleBulkSelect();
                                }
                            }}
                        />
                        <Button size="small" variant="outlined" onClick={handleBulkSelect}>
                            Add
                        </Button>
                    </Box>
                </Box>
                
            </Box>
            )}
            
            {/* General Actions */}
            <Box sx={{ mt: 2 }}>
                <Grid container spacing={1} alignItems="stretch">
                    {isAdmin && (
                        <Grid item xs={4}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={onManageLabels}
                                fullWidth
                                sx={{
                                    flexDirection: 'column',
                                    p: 1,
                                    height: '100%',
                                    fontSize: '0.7rem',
                                    lineHeight: 1.1,
                                    textAlign: 'center'
                                }}
                            >
                                <SettingsIcon sx={{ mb: 0.5, fontSize: '1.4rem' }} />
                                Manage Labels
                            </Button>
                        </Grid>
                    )}
                    {isAdmin && (
                        <Grid item xs={4}>
                            <Button
                                variant="outlined"
                                onClick={() => setAddDeviceDialogOpen(true)}
                                fullWidth
                                color="primary"
                                sx={{
                                    flexDirection: 'column',
                                    p: 1,
                                    height: '100%',
                                    fontSize: '0.7rem',
                                    lineHeight: 1.1,
                                    textAlign: 'center'
                                }}
                            >
                                <AddIcon sx={{ mb: 0.5, fontSize: '1.4rem' }} />
                                Add Device
                            </Button>
                        </Grid>
                    )}
                    <Grid item xs={isAdmin ? 4 : 12}>
                        <Button
                            variant="outlined"
                            onClick={onViewHistory}
                            fullWidth
                            sx={{
                                flexDirection: 'column',
                                p: 1,
                                height: '100%',
                                fontSize: '0.7rem',
                                lineHeight: 1.1,
                                textAlign: 'center'
                            }}
                        >
                            <HistoryIcon sx={{ mb: 0.5, fontSize: '1.4rem' }} />
                            View History
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            {/* Selection Mode Actions */}
            {isAdmin && selectionMode && selectedDevices.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    
                    <Grid container spacing={1}>
                        <Grid item xs={4}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={onLoadCharts}
                                fullWidth
                                sx={{ flexDirection: 'column', p: 1, height: '100%', fontSize: '0.7rem', lineHeight: 1.1, textAlign: 'center' }}
                            >
                                <ShowChartIcon sx={{ mb: 0.5, fontSize: '1.4rem' }} />
                                Load Charts
                            </Button>
                        </Grid>
                        <Grid item xs={4}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={onOpenScriptsPanel}
                                fullWidth
                                sx={{ flexDirection: 'column', p: 1, height: '100%', fontSize: '0.7rem', lineHeight: 1.1, textAlign: 'center' }}
                            >
                                <CodeIcon sx={{ mb: 0.5, fontSize: '1.4rem' }} />
                                Run Scripts
                            </Button>
                        </Grid>
                        <Grid item xs={4}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={onAddLabels}
                                fullWidth
                                sx={{ flexDirection: 'column', p: 1, height: '100%', fontSize: '0.7rem', lineHeight: 1.1, textAlign: 'center' }}
                            >
                                <LabelIcon sx={{ mb: 0.5, fontSize: '1.4rem' }} />
                                Add Labels
                            </Button>
                        </Grid>
                        <Grid item xs={4}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={onAddComment}
                                fullWidth
                                sx={{ flexDirection: 'column', p: 1, height: '100%', fontSize: '0.7rem', lineHeight: 1.1, textAlign: 'center' }}
                            >
                                <CommentIcon sx={{ mb: 0.5, fontSize: '1.4rem' }} />
                                Add Comment
                            </Button>
                        </Grid>
                        <Grid item xs={4}>
                            <Button
                                variant="outlined"
                                color="warning"
                                onClick={onReportIssue}
                                fullWidth
                                sx={{ flexDirection: 'column', p: 1, height: '100%', fontSize: '0.7rem', lineHeight: 1.1, textAlign: 'center' }}
                            >
                                <ReportProblemIcon sx={{ mb: 0.5, fontSize: '1.4rem' }} />
                                Report Issue
                            </Button>
                        </Grid>
                        <Grid item xs={4}>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={onRemoveDevices}
                                fullWidth
                                sx={{ flexDirection: 'column', p: 1, height: '100%', fontSize: '0.7rem', lineHeight: 1.1, textAlign: 'center' }}
                            >
                                <DeleteIcon sx={{ mb: 0.5, fontSize: '1.4rem' }} />
                                Remove
                            </Button>
                        </Grid>
                        <Grid item xs={4}>
                            <Button
                                variant="outlined"
                                color={filterListBySelection ? "primary" : "inherit"}
                                onClick={toggleFilterListBySelection}
                                fullWidth
                                sx={{ 
                                    flexDirection: 'column', 
                                    p: 1, 
                                    height: '100%', 
                                    fontSize: '0.7rem', 
                                    lineHeight: 1.1, 
                                    textAlign: 'center',
                                    borderColor: filterListBySelection ? 'primary.main' : alpha(theme.palette.divider, 0.5),
                                    backgroundColor: filterListBySelection ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                                }}
                            >
                                <FilterListIcon sx={{ mb: 0.5, fontSize: '1.4rem' }} />
                                {filterListBySelection ? 'Show All' : 'Filter Sel.'}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* Devices Summary */}
            <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                    Total Devices: {totalDevices}
                </Typography>
            </Box>

            <Dialog
                open={invalidBoxesDialogOpen}
                onClose={() => setInvalidBoxesDialogOpen(false)}
                aria-labelledby="invalid-devices-dialog-title"
            >
                <DialogTitle id="invalid-devices-dialog-title">
                    <ReportProblemIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Devices Not Found
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The following devices could not be found in the inventory and were not selected:
                    </DialogContentText>
                    <Paper variant="outlined" sx={{ p: 1.5, mt: 2, maxHeight: 200, overflow: 'auto', backgroundColor: alpha(theme.palette.background.default, 0.5) }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            {invalidBoxesToShow.join(', ')}
                        </Typography>
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInvalidBoxesDialogOpen(false)} color="primary" variant="contained" autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
});

export default ControlPanel; 